import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContentContainer } from '../components/HeroSectionStyles';
import ParticlesBackground from '../components/ParticlesBackground';

// Page transition animations
const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideOutToLeft = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-40px); }
`;

const slideOutToRight = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(40px); }
`;

// Reveal animation for staged content
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: 20px;
  position: relative;
  overflow: hidden;

  &.enter-right { animation: ${slideInFromRight} 280ms ease-out both; }
  &.enter-left { animation: ${slideInFromLeft} 280ms ease-out both; }
  &.exit-left { animation: ${slideOutToLeft} 240ms ease-in both; }
  &.exit-right { animation: ${slideOutToRight} 240ms ease-in both; }
`;

const Content = styled(ContentContainer)`
  max-width: 1100px;
  width: 92%;
  margin: 0 auto;
  padding: 48px 56px;
  border: 1px solid rgba(100, 182, 255, 0.25);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.18);
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.08);
  backdrop-filter: blur(8px);
`;

const SectionTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.6rem;
  color: #64B6FF;
  margin: 1.2rem 0 1rem;
  text-align: center;
  letter-spacing: 2px;
  text-shadow: 0 0 12px rgba(100, 182, 255, 0.5);
`;

const Description = styled.p`
  font-size: 1.35rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.92);
  text-align: center;
  margin: 12px auto 0;
`;

const GroupTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 1.2rem 0 0.6rem;
  text-align: center;
  letter-spacing: 1px;
`;

const LinksList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.25rem auto 0;
  max-width: 980px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LinkItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LinkAnchor = styled.a`
  color: #64B6FF;
  text-decoration: none;
  border-bottom: 1px dashed rgba(100, 182, 255, 0.35);
  padding-bottom: 2px;
  transition: color 160ms ease, border-color 160ms ease, text-shadow 160ms ease;

  &:hover {
    color: #9cd1ff;
    border-color: rgba(156, 209, 255, 0.6);
    text-shadow: 0 0 8px rgba(100, 182, 255, 0.45);
  }
`;

const RevealGroup = styled.div`
  animation: ${fadeUp} 320ms ease both;
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid rgba(100, 182, 255, 0.4);
  background: rgba(10, 18, 28, 0.6);
  color: #64B6FF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 0 12px rgba(100, 182, 255, 0.25);
  backdrop-filter: blur(4px);
  transition: transform 120ms ease, box-shadow 200ms ease, background 200ms ease;

  &:hover {
    transform: translateX(-2px);
    box-shadow: 0 0 16px rgba(100, 182, 255, 0.45);
    background: rgba(10, 18, 28, 0.75);
  }
`;

const NextButton = styled(BackButton)`
  left: auto;
  right: 20px;

  &:hover {
    transform: translateX(2px);
  }
`;

function ReuniaoOnboardingFormatoVideo() {
  const location = useLocation();
  const navigate = useNavigate();

  const [anim, setAnim] = useState(() => (location.state?.dir === 'back' ? 'enter-left' : 'enter-right'));
  const [revealStep, setRevealStep] = useState(0); // 0: nada, 1: refs, 2: docs, 3: exemplos

  const goBack = () => {
    setAnim('exit-right');
    setTimeout(() => {
      navigate(`/reuniao-onboarding/formatos${location.search}`, { state: { dir: 'back' } });
    }, 240);
  };

  const goNext = () => {
    setAnim('exit-left');
    setTimeout(() => {
      navigate(`/reuniao-onboarding/formatos/basicos${location.search}`, { state: { dir: 'forward' } });
    }, 240);
  };

  const handleAdvance = () => {
    setRevealStep((prev) => {
      if (prev < 3) return prev + 1;
      // Último passo já revelado: agora navega
      goNext();
      return prev;
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goBack();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleAdvance();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.search, revealStep]);

  return (
    <>
      <ParticlesBackground />
      <PageWrapper className={anim}>
        <BackButton onClick={(e) => { e.stopPropagation(); goBack(); }} aria-label="Voltar">‹</BackButton>
        <NextButton onClick={(e) => { e.stopPropagation(); handleAdvance(); }} aria-label="Avançar">›</NextButton>
        <Content onClick={handleAdvance}>
          <SectionTitle>Formato de Vídeo do Imóvel</SectionTitle>
          <Description>Tour com variações de aceleração</Description>

          {revealStep >= 1 && (
            <RevealGroup>
              <GroupTitle>Referências</GroupTitle>
              <LinksList>
                <LinkItem>
                  <LinkAnchor onClick={(e) => e.stopPropagation()} href="https://www.tiktok.com/@osmo_global/video/7469625278214442271?_t=ZM-8z8ui43apAz&_r=1" target="_blank" rel="noopener noreferrer">
                    Ref 1 — TikTok @osmo_global
                  </LinkAnchor>
                </LinkItem>
                <LinkItem>
                  <LinkAnchor onClick={(e) => e.stopPropagation()} href="https://www.tiktok.com/@mattcuzen/video/7501040124805827862?_r=1&_t=ZM-8z8unqqJQQS" target="_blank" rel="noopener noreferrer">
                    Ref 2 — TikTok @mattcuzen
                  </LinkAnchor>
                </LinkItem>
                <LinkItem>
                  <LinkAnchor onClick={(e) => e.stopPropagation()} href="https://www.tiktok.com/@fourhorsemenmedia/video/7519857786155470094?_r=1&_t=ZM-8z7Wpwtkyqm" target="_blank" rel="noopener noreferrer">
                    Ref 3 — TikTok @fourhorsemenmedia
                  </LinkAnchor>
                </LinkItem>
              </LinksList>
            </RevealGroup>
          )}

          {revealStep >= 2 && (
            <RevealGroup>
              <GroupTitle>Docs com fotos do imóvel</GroupTitle>
              <LinksList>
                <LinkItem>
                  <LinkAnchor onClick={(e) => e.stopPropagation()} href="https://drive.google.com/drive/folders/1jspVaRDsDO_nas3cEVjxZWb35YZDQ_0P" target="_blank" rel="noopener noreferrer">
                    Fotos do imóvel (Drive)
                  </LinkAnchor>
                </LinkItem>
              </LinksList>
            </RevealGroup>
          )}

          {revealStep >= 3 && (
            <RevealGroup>
              <GroupTitle>Vídeos — exemplos:</GroupTitle>
              <LinksList>
                <LinkItem>
                  <LinkAnchor onClick={(e) => e.stopPropagation()} href="https://drive.google.com/drive/folders/1jspVaRDsDO_nas3cEVjxZWb35YZDQ_0P" target="_blank" rel="noopener noreferrer">
                    Exemplo 16:9
                  </LinkAnchor>
                </LinkItem>
                <LinkItem>
                  <LinkAnchor onClick={(e) => e.stopPropagation()} href="https://drive.google.com/file/d/1YANJFfoqbQWT8p7PcjonroRURPAeol2w/view?usp=sharing" target="_blank" rel="noopener noreferrer">
                  Exemplo 9:16
                  </LinkAnchor>
                </LinkItem>
              </LinksList>
            </RevealGroup>
          )}
        </Content>
      </PageWrapper>
    </>
  );
}

export default ReuniaoOnboardingFormatoVideo;
